<template>
<div :class="[wrapperClass, activeClass, errorClass]">
    <label v-if="label">{{ label }}</label>
    <div class="input-group-icon">
        <input
            name="password"
            v-model="password"
            :type="inputType"
            :class="inputClass"
            :disabled="disabled"
            :placeholder="inputPlaceholder"
            :tabindex="tabIndex"
            @input="handleOnChangeInputPassword"
            @blur="handleOnInputBlur"
            @focus="handleOnInputFocus"
            @keyup.enter="handleOnInputEnter"
        >
        <i :class="visibleIcon" @click="handleShowPassword"></i>
    </div>
    <p v-if="!replaceLabelWithError" class="error-text">
        {{ errorMessage }}
    </p>
    <div v-if="showPasswordSuggestion && !reseted && needPasswordSuggestion">
        <div class="strength-bar">
            <div class="bar" :style="barSize" :class="barColor"><div class="bar-text">{{text[Math.max(0, score-1)]}}</div></div>
        </div>
        <p class="feedback-warning" v-if="showFeedbackMessage">
            {{ feedback.warning }}
        </p>
        <div class="feedback-suggestion" v-if="showFeedbackMessage">
            <ul :class="{'double-columns': feedback.suggestions.length > 1}">
                <li
                    v-for="(suggestion, i) in feedback.suggestions"
                    :key="i"
                    :class="{'is-fulfilled': suggestion.isFulfilled}">
                        {{ suggestion.title }}
                </li>
            </ul>
        </div>
    </div>
</div>
</template>

<script>
import zxcvbn from 'zxcvbn';
import { every } from 'lodash';

export default {
    name: 'password-input-with-suggestion',
    props: {
        inputLabel: String,
        wrapperClass: String,
        inputClass: String,
        inputName: String,
        inputPlaceholder: String,
        tabIndex: {
            type: Number,
            default: 1,
        },
        checkErrors: Boolean,
        isShowPassword: Boolean,
        isDisabled: Boolean,
        isReseted: Boolean,
        replaceLabelWithError: {
            type: Boolean,
            default: true,
        },
        needPasswordSuggestion: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            label: '',
            password: '',
            showPassword: false,
            colors: ['bg-primary', 'bg-danger', 'bg-warning', 'bg-success'],
            text: ['Weak', 'Average', 'Strong', 'Very Strong'],
            active: false,
            hasError: false,
            showPasswordSuggestion: false,
            disabled: false,
            reseted: false,
            errorMessage: '',
        };
    },
    mounted() {
        this.label = this.inputLabel;
        this.showPassword = this.isShowPassword;
        this.disabled = this.isDisabled;
        this.reseted = this.isReseted;
    },
    computed: {
        inputType() {
            return this.showPassword ? 'text' : 'password';
        },
        visibleIcon() {
            return this.showPassword ? 'fa fa-eye-slash' : 'fa fa-eye';
        },
        basicPasswordCheck() {
            const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$/gm;
            const regexSpace = /\s/;
            return regex.test(this.password) && !regexSpace.test(this.password);
        },
        lengthAndSpecialCharacterCheck() {
            const regexSP = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
            return (this.password.length > 7 && regexSP.test(this.password));
        },
        activeClass() {
            return this.active ? 'active' : '';
        },
        errorClass() {
            return this.hasError ? 'has-error' : '';
        },
        score() {
            const regex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{1,}$/gm;
            if (regex.test(this.password)) {
                if (this.lengthAndSpecialCharacterCheck) {
                    if (this.basicPasswordCheck) {
                        const { score } = zxcvbn(this.password);
                        return score;
                    }
                    return 2;
                }
                return 1;
            }
            return 0;
        },
        passwordLengthCheck() {
            return this.password.length >= 8;
        },
        feedback() {
            const suggestions = [
                {
                    title: 'At least 8 characters',
                    isFulfilled: this.passwordLengthCheck,
                },
                {
                    title: 'One uppercase letter',
                    isFulfilled: this.checkRegex(/[A-Z]/),
                },
                {
                    title: 'One lowercase letter',
                    isFulfilled: this.checkRegex(/[a-z]/),
                },
                {
                    title: 'One number',
                    isFulfilled: this.checkRegex(/[0-9]/),
                },
                {
                    title: 'One special character',
                    isFulfilled: this.checkRegex(/[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/),
                },
                {
                    title: 'No empty space',
                    isFulfilled: !this.checkRegex(/\s/),
                },
            ];

            const fulfilledBasicCheck = every(suggestions, 'isFulfilled');
            if (fulfilledBasicCheck) {
                const { feedback } = zxcvbn(this.password);
                feedback.suggestions = feedback.suggestions.map((suggestion) => ({
                    title: suggestion,
                    isFulfilled: false,
                }));
                return feedback;
            }

            return {
                warning: '',
                suggestions,
            };
        },
        barSize() {
            // eslint-disable-next-line no-mixed-operators
            return `flex-basis: ${this.score / 4 * 100}%;`;
        },
        barColor() {
            const full = this.score === 4 ? ' full' : '';
            return this.colors[Math.min(this.score, 3)] + full;
        },
        showFeedbackMessage() {
            return Object.values(this.feedback)[1].length > 0;
        },
    },
    methods: {
        handleOnChangeInputPassword(e) {
            this.$emit('onPasswordChange', e.target.value);
            this.setPasswordErrorMessage();
            this.$emit('onPasswordValidation', this.score);
        },
        handleOnInputBlur(e) {
            this.password = e.target.value;
            this.setPasswordErrorMessage();
        },
        handleOnInputFocus() {
            this.active = true;
            this.showPasswordSuggestion = true;
            this.reseted = false;
        },
        handleOnInputEnter() {
            this.$emit('onPasswordEnter');
        },
        setPasswordErrorMessage() {
            let message;
            let isError;
            let isActive;
            if (((this.password === '') || (this.password === null)) && this.checkErrors) {
                message = 'Field cannot be empty';
                isError = true;
                isActive = false;
            } else {
                message = this.replaceLabelWithError ? this.inputLabel : '';
                isError = false;
                isActive = true;
            }

            if (this.replaceLabelWithError) {
                this.label = message;
            } else {
                this.errorMessage = message;
            }
            this.hasError = isError;
            this.active = isActive;
        },
        handleShowPassword() {
            this.showPassword = !this.showPassword;
            this.$emit('onShowPassword', this.showPassword);
        },
        checkRegex(regex) {
            return regex.test(this.password);
        },
    },
    watch: {
        isShowPassword(value) {
            this.showPassword = value;
        },
        isDisabled(value) {
            this.disabled = value;
        },
        isReseted(value) {
            if (value) {
                this.reseted = true;
                this.password = '';
                this.showPasswordSuggestion = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@mixin default-input-style {
    color: $input-focus-color;
    background-color: $input-focus-bg;
    outline: 0;
    box-shadow: none;
}

.input-set {
    &.active {
        label {
            color: $primary !important;
        }

        .form-control, .form-control:focus {
            @include default-input-style;
            border-color: $primary !important;
        }
    }
    &.has-error {
        label {
            color: $red !important;
        }

        .form-control, .form-control:focus {
            @include default-input-style;
            border-color: $red !important;
        }
    }
}

.form-account .form-control {
    border-color: #E7D6D5 !important;
}

.input-group-icon {
    position: relative;

    i {
        cursor: pointer;
        position: absolute;
        top: calc(50% - .5rem);
        right: 0.625rem;
        color: rgba(0,0,0,0.4);
        font-size: 1rem;
    }
}

.strength-bar {
    margin-top: 0.5rem;
    display: flex;
    width: 100%;
    min-height: 1rem;
    background: $gray-200;
    border-radius: 5rem !important;
    .bar {
        color: white;
        font-weight: 600;
        text-align: center;
        justify-content: center;
        padding: 0 0.5rem;
        height: 100%;
        display: flex;
        align-items: center;
        border-radius: 5rem 0 0 5rem;
        transition: flex-basis 0.2s;
        &.full {
            border-radius: 5rem;
        }
    }
}

p.error-text {
    font-size: 0.9rem;
    color: $red;
}

p.feedback-warning {
    font-size: .8rem;
    color: #d22832;
    margin-bottom: 0;
}

.feedback-suggestion {
    font-size: .8rem;
    ul {
        margin-bottom: 0;
        padding: .5rem 0 .5rem 0rem;
        list-style-position: inside;

        &.double-columns {
            columns: 2;
            -webkit-columns: 2;
            -moz-columns: 2;
        }

        li {
            color: $color-neutral-dark-100;

            &.is-fulfilled {
                color: $color-neutral-dark-500;
            }
        }
    }
}
</style>
